import { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  clearSuppressPostLoginReturnTo,
  DEFAULT_POST_LOGIN_PATH,
  pathWithReturnTo,
  readPostLoginRedirect,
  resolvePostLoginPath,
  shouldIgnoreLoginReturnTo,
} from '../utils/navigationReturn';
import logoZra from '../../media/logo_zra.png';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, completeMfaLogin, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const ignoreReturnTo = shouldIgnoreLoginReturnTo();
  const postLoginPath = ignoreReturnTo ? DEFAULT_POST_LOGIN_PATH : resolvePostLoginPath(location);
  const forgotPasswordHref = ignoreReturnTo
    ? '/forgot-password'
    : pathWithReturnTo('/forgot-password', readPostLoginRedirect(location));

  useEffect(() => {
    if (!shouldIgnoreLoginReturnTo()) return;
    if (readPostLoginRedirect(location)) {
      navigate('/login', { replace: true });
      return;
    }
    clearSuppressPostLoginReturnTo();
  }, [location, navigate]);

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      navigate(postLoginPath, { replace: true });
    }
  }, [authLoading, isAuthenticated, navigate, postLoginPath]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (mfaToken) {
      const result = await completeMfaLogin(mfaToken, mfaCode);
      if (result.success) {
        navigate(postLoginPath, { replace: true });
      } else {
        setError(result.error || 'Invalid verification code.');
      }
      setLoading(false);
      return;
    }

    const result = await login(username, password);

    if (result.success) {
      navigate(postLoginPath, { replace: true });
    } else if (result.mfaRequired && result.mfaToken) {
      setMfaToken(result.mfaToken);
      setMfaCode('');
      setError('');
    } else {
      setError(result.error || 'Login failed. Please try again.');
    }

    setLoading(false);
  };

  const backToPassword = () => {
    setMfaToken('');
    setMfaCode('');
    setError('');
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center">
            <img
              src={logoZra}
              alt="ZRA Logo"
              className="h-24 w-auto"
            />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Zan Gateway
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {mfaToken ? 'Enter the code from your authenticator app' : 'Sign in to access your dashboard'}
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              <p className="font-medium">Login Error</p>
              <p className="text-sm mt-1">{error}</p>
            </div>
          )}

          {!mfaToken ? (
            <div className="rounded-md shadow-sm -space-y-px">
              <div>
                <label htmlFor="username" className="sr-only">
                  Username or email
                </label>
                <input
                  id="username"
                  name="username"
                  type="text"
                  required
                  autoComplete="username"
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Username or email"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                />
              </div>
              <div>
                <label htmlFor="password" className="sr-only">
                  Password
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 focus:z-10 sm:text-sm"
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>
          ) : (
            <div>
              <label htmlFor="mfaCode" className="sr-only">
                Authentication code
              </label>
              <input
                id="mfaCode"
                name="mfaCode"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                required
                maxLength={6}
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm tracking-widest text-center text-lg"
                placeholder="000000"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </div>
          )}

          {!mfaToken && (
            <div className="flex justify-end">
              <Link to={forgotPasswordHref} className="text-sm text-primary-600 hover:text-primary-500">
                Forgot password?
              </Link>
            </div>
          )}

          <div className="space-y-2">
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Please wait…' : mfaToken ? 'Verify and sign in' : 'Sign in'}
            </button>
            {mfaToken && (
              <button
                type="button"
                onClick={backToPassword}
                className="w-full text-sm text-gray-600 hover:text-gray-800"
              >
                Back to password
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
